import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api'
import {
  GreyGrey70,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  useNotifications,
  SimpleModal,
  Box
} from '@sequencehq/core-components'
import { apiDatesAdapters } from '@sequencehq/utils/dates'
import type { DeferredRevenueTableGroupBy } from './content/DeferredRevenueDashboardTable'
import { DEFERRED_REVENUE_EXPORT_COPY } from './deferredRevenueExport.constants'
import { extractApiErrorMessage } from '../common/extractApiErrorMessage'

type Props = {
  trigger: React.ReactElement
  asOfDate: Date
  groupBy: DeferredRevenueTableGroupBy
  customerIds: string[]
  productIds: string[]
  invoiceIds: string[]
  excludeFullyRecognized?: boolean
}

export const ExportDeferredRevenueScheduleModal = (props: Props) => {
  const { displayNotification } = useNotifications()

  const postDeferredRevenueScheduleReportMutation = useMutation(
    dashboardv99990101Client.postDeferredRevenueScheduleReport
  )

  const onSubmit = async () => {
    try {
      await postDeferredRevenueScheduleReportMutation.mutateAsync({
        fileFormat: 'CSV',
        // Spec marks excludeFullyRecognized as required with a default of
        // true, so always send it even when the page filter is off.
        excludeFullyRecognized: props.excludeFullyRecognized ?? true,
        asOfDate: apiDatesAdapters.toApi(props.asOfDate),
        ...(props.groupBy ? { groupBy: props.groupBy } : {}),
        ...(props.customerIds.length > 0
          ? { customerIds: props.customerIds }
          : {}),
        ...(props.productIds.length > 0
          ? { productIds: props.productIds }
          : {}),
        ...(props.invoiceIds.length > 0 ? { invoiceIds: props.invoiceIds } : {})
      })
      displayNotification(DEFERRED_REVENUE_EXPORT_COPY.notifications.success, {
        type: 'success'
      })
      return true
    } catch (e) {
      displayNotification(
        extractApiErrorMessage(
          e,
          DEFERRED_REVENUE_EXPORT_COPY.notifications.error
        ),
        { type: 'error' }
      )
      return false
    }
  }

  return (
    <SimpleModal
      minHeight="0"
      title={DEFERRED_REVENUE_EXPORT_COPY.modal.title}
      trigger={props.trigger}
      cancelButtonText={DEFERRED_REVENUE_EXPORT_COPY.modal.cancelLabel}
      submitButtonText={DEFERRED_REVENUE_EXPORT_COPY.modal.submitLabel}
      submittingButtonText={DEFERRED_REVENUE_EXPORT_COPY.modal.submitLabel}
      data-testid="defrev.modal.exportCSV"
      onSubmit={onSubmit}
    >
      <Box {...Primary13Regular} color={GreyGrey70}>
        {DEFERRED_REVENUE_EXPORT_COPY.modal.body}{' '}
        <Link
          to={DEFERRED_REVENUE_EXPORT_COPY.docs.href}
          target="_blank"
          rel="noopener noreferrer"
          color={IndigoIndigo60}
        >
          {DEFERRED_REVENUE_EXPORT_COPY.docs.label}
        </Link>
      </Box>
    </SimpleModal>
  )
}
